/**
 * Generic pub/sub bus for side-panel open/close coordination.
 *
 * Each panel module creates its own bus via ``createPanelBus<T>()``.
 * The bus handles:
 *   - dispatching typed open requests to subscribers
 *   - dispatching a close signal (``null``) to subscribers
 *   - subscriber management (add/remove)
 *
 * Components subscribe in a ``useEffect`` and receive either a typed
 * request (open) or ``null`` (close).  The ``__close__`` sentinel
 * logic is fully encapsulated here, keeping components simple.
 */

/** Callback that receives an open request or ``null`` for close. */
export type PanelListener<T> = (request: T | null) => void;

export interface PanelBus<T> {
  /** Dispatch a typed open request to all subscribers. */
  open: (request: T) => void;
  /** Dispatch a close signal (``null``) to all subscribers. */
  close: () => void;
  /** Subscribe to open/close events. Returns an unsubscribe function. */
  subscribe: (listener: PanelListener<T>) => () => void;
}

/** Create a typed panel bus for a specific panel's request shape. */
export function createPanelBus<T>(): PanelBus<T> {
  const listeners = new Set<PanelListener<T>>();

  return {
    open(request: T) {
      for (const listener of listeners) {
        listener(request);
      }
    },
    close() {
      for (const listener of listeners) {
        listener(null);
      }
    },
    subscribe(listener: PanelListener<T>) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
